import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useMessages } from "../hooks/useMessages";
import { useMembers } from "../hooks/useMembers";
import { usePushNotifications } from "../hooks/usePushNotifications";
import { useAnalytics } from "../hooks/useAnalytics";
import { qrPostMessageFn, qrGetRoomDetailsFn } from "../firebase";
import { vibrate } from "../utils/platform";
import { RoomDetails } from "../types";

interface ChatRoomProps {
  roomCode: string;
  user: any;
  onBack: () => void;
  onLocationCheck?: () => Promise<boolean>;
}

/**
 * ChatRoom Component - Figma Design
 * Dark theme with cyan message bubbles and avatars
 */
export function ChatRoom({ roomCode, user, onBack, onLocationCheck }: ChatRoomProps) {
  const { t } = useTranslation();
  const [messageText, setMessageText] = useState("");
  const [sending, setSending] = useState(false);
  const [roomDetails, setRoomDetails] = useState<RoomDetails | null>(null);
  const [showMembers, setShowMembers] = useState(false);
  const [locationCheckFailed, setLocationCheckFailed] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const enterTimeRef = useRef<number>(Date.now());
  const messageCountRef = useRef<number>(0);

  const { messages, loading: messagesLoading, error: messagesError } = useMessages(roomCode);
  const { members, loading: membersLoading, refetch: refetchMembers } = useMembers(roomCode, user);
  const analytics = useAnalytics();
  
  usePushNotifications({
    roomCode,
    roomName: roomDetails?.code || roomCode,
    currentUserUid: user?.uid,
    messages,
  });
  
  // Track room entry
  useEffect(() => {
    analytics.logRoomEntered(roomCode, "home");
    enterTimeRef.current = Date.now();
    messageCountRef.current = 0;
  }, [roomCode]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Fetch room details
  useEffect(() => {
    if (roomCode && user) {
      qrGetRoomDetailsFn({ code: roomCode })
        .then((result: any) => setRoomDetails(result))
        .catch((err) => console.error("Failed to load room details:", err));
    }
  }, [roomCode, user]);

  // Periodic location check every 5 minutes
  useEffect(() => {
    if (!onLocationCheck) return;

    const checkLocation = async () => {
      const isValid = await onLocationCheck();
      setLocationCheckFailed(!isValid);
    };

    const interval = setInterval(checkLocation, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [onLocationCheck]);

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!messageText.trim() || sending || !user) return;

    const deviceId = localStorage.getItem("BlinkBli:deviceId") || crypto.randomUUID();
    const clientMessageId = `${deviceId}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    setSending(true);
    vibrate(10);

    try {
      await qrPostMessageFn({
        code: roomCode,
        deviceId,
        text: messageText.trim(),
        clientMessageId,
      });

      // Track message sent
      analytics.logMessageSent(roomCode, messageText.trim().length);
      messageCountRef.current += 1;
      
      setMessageText("");
      // Keep focus on input for continuous typing
      inputRef.current?.focus();
    } catch (err: any) {
      console.error("Failed to send message:", err);
      alert(`Failed to send message: ${err.message || "Unknown error"}`);
    } finally {
      setSending(false);
    }
  };
  
  // Handle back with analytics
  const handleBack = () => {
    const durationSeconds = Math.floor((Date.now() - enterTimeRef.current) / 1000);
    analytics.logChatExited(roomCode, durationSeconds, messageCountRef.current);
    onBack();
  };

  const formatDate = (isoString: string) => {
    const date = new Date(isoString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return "Today";
    } else if (date.toDateString() === yesterday.toDateString()) {
      return "Yesterday";
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
  };

  const isCurrentUser = (senderUid: string) => user?.uid === senderUid;

  const handleToggleMembers = () => {
    setShowMembers(!showMembers);
    vibrate(5);
    if (!showMembers) refetchMembers();
  };

  return (
    <div className="chat-room">
      {/* Header */}
      <div className="chat-room__header">
        <button onClick={handleBack} className="chat-room__back-btn">
          ← {t("back")}
        </button>
        <div className="chat-room__info">
          <h2 className="chat-room__title">{t("appName")}</h2>
        </div>
        <button onClick={handleToggleMembers} className="chat-room__members-btn">
          👥
        </button>
      </div>

      {/* Location warning */}
      {locationCheckFailed && (
        <div className="chat-room__warning">
          ⚠️ {t("locationWarning")}
        </div>
      )}

      {/* Members sidebar */}
      {showMembers && (
        <div className="chat-room__members-sidebar">
          <div className="chat-room__members-header">
            <h3>{t("members")} {roomDetails && `(${roomDetails.numberOfUsers})`}</h3>
            <button onClick={() => setShowMembers(false)}>✕</button>
          </div>
          {membersLoading ? (
            <div className="chat-room__members-loading">{t("loading")}</div>
          ) : (
            <div className="chat-room__members-list">
              {members.map((member) => (
                <div key={member.uid} className="chat-room__member">
                  <span className="chat-room__member-name">{member.username}</span>
                  {isCurrentUser(member.uid) && (
                    <span className="chat-room__member-badge">{t("you")}</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Messages */}
      <div className="chat-room__messages">
        {messagesLoading && messages.length === 0 ? (
          <div className="chat-room__loading">{t("loadingMessages")}</div>
        ) : messagesError ? (
          <div className="chat-room__error">{t("error")}: {messagesError}</div>
        ) : messages.length === 0 ? (
          <div className="chat-room__empty">
            {t("noMessages")}
          </div>
        ) : (
          <>
            {messages.map((msg, index) => {
              const showDate = index === 0 ||
                formatDate(messages[index - 1].createdAt) !== formatDate(msg.createdAt);

              return (
                <div key={msg.id}>
                  {showDate && (
                    <div className="chat-room__date-separator">
                      {formatDate(msg.createdAt)}
                    </div>
                  )}
                  <div
                    className={`chat-room__message ${
                      isCurrentUser(msg.senderUid)
                        ? 'chat-room__message--own'
                        : 'chat-room__message--other'
                    }`}
                  >
                    {/* Avatar for other users */}
                    {!isCurrentUser(msg.senderUid) && (
                      <div className="chat-room__message-avatar" />
                    )}
                    
                    <div className="chat-room__message-content">
                      {!isCurrentUser(msg.senderUid) && (
                        <div className="chat-room__message-sender">{msg.senderName}</div>
                      )}
                      <div className="chat-room__message-bubble">
                        <div className="chat-room__message-text">{msg.text}</div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Message input - Glass pill style */}
      <form className="chat-room__input-form" onSubmit={handleSendMessage}>
        <div className="chat-room__input-container">
          <input
            ref={inputRef}
            type="text"
            value={messageText}
            onChange={(e) => setMessageText(e.target.value)}
            placeholder={t("placeholder")}
            className="chat-room__input"
            disabled={sending}
            maxLength={500}
          />
          <button
            type="submit"
            disabled={!messageText.trim() || sending}
            className={`chat-room__send-btn ${messageText.trim() ? 'chat-room__send-btn--has-text' : ''}`}
          />
        </div>
      </form>
    </div>
  );
}
